import React, { FC, useMemo } from 'react';
import { ProductMetaData, ProductSorting } from '../../../../models/Product';
import { getLoanFormats, LoanFormat, mapLoanFormat, mapParsedLoanFormat } from '../../../../models/LoanFormat';
import { FilterSetting, ProductFilter, ProductSpec } from '../../../store/api';
import { BackIssues, CatalogueProductGroup, LanguageLearningProductGroup, LoteProductGroup } from '../ProductGroup';
import RoutingUtils from '../../utils/utils-routing';
import Utils from '../../utils';
import {
    PRODUCT_CLASS_CAMPAIGN,
    PRODUCT_CLASS_LANGUAGE_LEARNING,
    PRODUCT_CLASS_LOCAL,
    PRODUCT_CLASS_LOTE,
    PRODUCT_CLASS_STANDARD,
    ProductClass
} from '../../../store/constants';
import { useTranslation } from 'react-i18next';
import { MULTI_LANGUAGE_SHOP, useFeature } from '../../utils/togglz';
import MetadataUtils from '../../utils/utils-metadata';
import useShopLanguages from '../../hooks/useShopLanguages';
import { ALL_LANGUAGES, OTHER_LANGUAGES } from '../../../shop/language/constants';
import { LOANFORMAT_EPRESS_OBJECT } from '../../loanFormat/constants';
import { To } from 'react-router-dom';

type Props = {
    readonly product: ProductMetaData;
};

const isMultiLanguageStandardClass = (value: ProductClass) => {
    return [PRODUCT_CLASS_LOTE, PRODUCT_CLASS_STANDARD].includes(value);
};

// TODO [MultiLanguageShop] Remove this component when MultiLanguageShop is enabled
export const ProductDetailProductGroups: FC<Props> = ({ product }) => {
    const productLoanFormat = mapLoanFormat(product.loanFormat)!;
    const otherLoanFormats = getLoanFormats().filter(loanFormat => loanFormat !== productLoanFormat);

    return (
        <>
            {productLoanFormat === LOANFORMAT_EPRESS_OBJECT.name ? <BackIssues product={product} /> : null}
            <SeriesProductGroup loanFormat={productLoanFormat} product={product} dataSource={'catalogue'} />
            <AuthorProductGroup loanFormat={productLoanFormat} product={product} dataSource={'catalogue'} />
            <GenreProductGroup loanFormat={productLoanFormat} product={product} dataSource={'catalogue'} />

            {otherLoanFormats.map(loanFormat => (
                <React.Fragment key={loanFormat}>
                    <SeriesProductGroup loanFormat={loanFormat} product={product} dataSource={'catalogue'} />
                    <AuthorProductGroup loanFormat={loanFormat} product={product} dataSource={'catalogue'} />
                    <GenreProductGroup loanFormat={loanFormat} product={product} dataSource={'catalogue'} />
                </React.Fragment>
            ))}
        </>
    );
};

// TODO [MultiLanguageShop] Remove this redundant component when MultiLanguageShop is enabled
export const ForeignLanguageProductDetailProductGroups: FC<Props> = ({ product }) => {
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);
    const productLoanFormat = mapLoanFormat(product.loanFormat)!;
    const otherLoanFormats = getLoanFormats().filter(loanFormat => loanFormat !== productLoanFormat);
    const dataSource =
        product.productClass === PRODUCT_CLASS_LOTE
            ? isMultiLanguageShopEnabled
                ? 'catalogue'
                : 'lote'
            : 'languagelearning';

    return (
        <>
            {productLoanFormat === LOANFORMAT_EPRESS_OBJECT.name ? <BackIssues product={product} /> : null}

            <SeriesProductGroup
                loanFormat={productLoanFormat}
                product={product}
                language={product.language}
                dataSource={dataSource}
            />
            <AuthorProductGroup
                loanFormat={productLoanFormat}
                product={product}
                language={product.language}
                dataSource={dataSource}
            />
            <GenreProductGroup
                loanFormat={productLoanFormat}
                product={product}
                language={product.language}
                dataSource={dataSource}
            />

            <SeriesProductGroup
                loanFormat={productLoanFormat}
                product={product}
                language={OTHER_LANGUAGES}
                dataSource={dataSource}
            />
            <AuthorProductGroup
                loanFormat={productLoanFormat}
                product={product}
                language={OTHER_LANGUAGES}
                dataSource={dataSource}
            />
            <GenreProductGroup
                loanFormat={productLoanFormat}
                product={product}
                language={OTHER_LANGUAGES}
                dataSource={dataSource}
            />

            {otherLoanFormats.map(loanFormat => (
                <React.Fragment key={loanFormat}>
                    <SeriesProductGroup
                        loanFormat={loanFormat}
                        product={product}
                        language={product.language}
                        dataSource={dataSource}
                    />
                    <AuthorProductGroup
                        loanFormat={loanFormat}
                        product={product}
                        language={product.language}
                        dataSource={dataSource}
                    />
                    <GenreProductGroup
                        loanFormat={loanFormat}
                        product={product}
                        language={product.language}
                        dataSource={dataSource}
                    />
                    <SeriesProductGroup
                        loanFormat={loanFormat}
                        product={product}
                        language={OTHER_LANGUAGES}
                        dataSource={dataSource}
                    />
                    <AuthorProductGroup
                        loanFormat={loanFormat}
                        product={product}
                        language={OTHER_LANGUAGES}
                        dataSource={dataSource}
                    />
                    <GenreProductGroup
                        loanFormat={loanFormat}
                        product={product}
                        language={OTHER_LANGUAGES}
                        dataSource={dataSource}
                    />
                </React.Fragment>
            ))}
        </>
    );
};

export const MultiLanguageShopProductDetailProductGroups: FC<Props> = ({ product }) => {
    const { shopLanguages, mapShopLanguageIdByLanguageName, showLote } = useShopLanguages();
    const productLoanFormat = mapParsedLoanFormat(product.loanFormat);

    const dataSource = isMultiLanguageStandardClass(product.productClass) ? 'catalogue' : 'languagelearning';

    const shopLanguageNames = shopLanguages.map(language => language.shopLanguageName);
    const sortedLoanFormats = [
        product.loanFormat,
        ...getLoanFormats().filter(lf => lf !== product.loanFormat && lf !== LoanFormat.ePress),
        LoanFormat.ePress
    ];
    const sortedLanguage = [
        product.language ?? '',
        ...shopLanguageNames.filter(sl => sl !== product.language && sl !== OTHER_LANGUAGES),
        OTHER_LANGUAGES
    ];

    const bookFormats = getLoanFormats().filter(loanFormat => loanFormat !== LoanFormat.ePress);

    return productLoanFormat === LoanFormat.ePress ? (
        <>
            <BackIssues product={product} />
            {sortedLanguage.map(language => (
                <React.Fragment key={`genre-${language}`}>
                    <GenreProductGroup
                        loanFormat={productLoanFormat}
                        product={product}
                        dataSource={dataSource}
                        language={language}
                        shopLanguages={shopLanguageNames}
                        languageId={mapShopLanguageIdByLanguageName(language)}
                        showLote={showLote}
                    />
                </React.Fragment>
            ))}
            {bookFormats.map(loanFormat => (
                <React.Fragment key={loanFormat}>
                    <GenreProductGroup
                        loanFormat={loanFormat}
                        product={product}
                        language={product.language}
                        dataSource={dataSource}
                        shopLanguages={shopLanguageNames}
                        languageId={mapShopLanguageIdByLanguageName(product.language)}
                        showLote={showLote}
                    />
                </React.Fragment>
            ))}
        </>
    ) : (
        <>
            {sortedLoanFormats.map(loanFormat => (
                <React.Fragment key={`series-author-${loanFormat}`}>
                    <SeriesProductGroup
                        loanFormat={loanFormat}
                        product={product}
                        dataSource={dataSource}
                        language={product.language}
                        shopLanguages={shopLanguageNames}
                        languageId={mapShopLanguageIdByLanguageName(product.language)}
                        showLote={showLote}
                    />
                    {sortedLanguage.map(language => (
                        <React.Fragment key={`author-${language}`}>
                            <AuthorProductGroup
                                loanFormat={loanFormat}
                                product={product}
                                dataSource={dataSource}
                                language={language}
                                shopLanguages={shopLanguageNames}
                                languageId={mapShopLanguageIdByLanguageName(language)}
                                showLote={showLote}
                            />
                        </React.Fragment>
                    ))}
                </React.Fragment>
            ))}
            {sortedLoanFormats.map(loanFormat => (
                <React.Fragment key={`genre-${loanFormat}`}>
                    <GenreProductGroup
                        loanFormat={loanFormat}
                        product={product}
                        dataSource={dataSource}
                        language={product.language}
                        shopLanguages={shopLanguageNames}
                        languageId={mapShopLanguageIdByLanguageName(product.language)}
                        showLote={showLote}
                    />
                </React.Fragment>
            ))}
        </>
    );
};

type SeriesProductGroupProps = {
    readonly loanFormat: LoanFormat;
    readonly product: ProductMetaData;
    readonly language?: string;
    readonly dataSource: 'catalogue' | 'lote' | 'languagelearning';
    shopLanguages?: string[];
    languageId?: string;
    showLote?: boolean;
};
const SeriesProductGroup: FC<SeriesProductGroupProps> = ({
    loanFormat,
    product,
    language,
    dataSource,
    shopLanguages,
    languageId,
    showLote
}) => {
    const productLoanFormat = mapLoanFormat(product.loanFormat)!;
    const languageFilter = useMemo(
        () => buildLanguageFilter(product, language, shopLanguages),
        [product, language, shopLanguages]
    );
    const { t } = useTranslation();
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);
    const shopLanguage = language === OTHER_LANGUAGES ? language : languageId;

    const productSpec: ProductSpec = useMemo<ProductSpec>(
        () => ({
            loanFormat,
            filters: {
                series: { include: [product.seriesName!] },
                productId: { exclude: [product.id] },
                ...languageFilter
            },
            sortBy: ProductSorting.SERIES_NUMBER_HIGH_TO_LOW
        }),
        [product, languageFilter, loanFormat]
    );

    const seeAllUrl = buildSeeAllUrl({
        product,
        loanFormat,
        filter: productSpec.filters!,
        isMultiLanguageShopEnabled,
        shopLanguage
    });

    const titlePrefix = loanFormat === productLoanFormat ? t('components.productDetails.more') : '';

    const title = t(`components.productDetails.moreFrom_product`, {
        titlePrefix,
        loanFormat: t(`components.productDetails.${loanFormat}`),
        language: t(`filters:language.${language}`, {
            defaultValue:
                language === OTHER_LANGUAGES && showLote
                    ? t('menu.shop.shopLanguages.lote_dropdown')
                    : t('otherLanguages')
        }),
        type: product.seriesName
    });

    if (!product.seriesName) {
        return null;
    }

    switch (dataSource) {
        case 'lote':
            return <LoteProductGroup productSpec={productSpec} seeAllUrl={seeAllUrl} title={title.trim()} />;
        case 'languagelearning':
            return (
                <LanguageLearningProductGroup
                    productSpec={productSpec}
                    seeAllUrl={seeAllUrl}
                    title={title.trim()}
                    shopLanguage={undefined}
                />
            );
        default:
            return (
                <CatalogueProductGroup
                    productSpec={productSpec}
                    seeAllUrl={seeAllUrl}
                    title={title.trim()}
                    shopLanguage={shopLanguage}
                />
            );
    }
};

type AuthorProductGroupProps = {
    readonly loanFormat: LoanFormat;
    readonly product: ProductMetaData;
    readonly language?: string;
    readonly dataSource: 'catalogue' | 'lote' | 'languagelearning';
    shopLanguages?: string[];
    languageId?: string;
    showLote?: boolean;
};
const AuthorProductGroup: FC<AuthorProductGroupProps> = ({
    loanFormat,
    product,
    language,
    dataSource,
    shopLanguages,
    languageId,
    showLote
}) => {
    const productLoanFormat = mapLoanFormat(product.loanFormat)!;
    const languageFilter = useMemo(
        () => buildLanguageFilter(product, language, shopLanguages),
        [product, language, shopLanguages]
    );
    const { t } = useTranslation();
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);
    const shopLanguage = language === OTHER_LANGUAGES ? 'other' : languageId;

    const authors = useMemo(() => (product.authors ? product.authors.filter(a => !!a.id) : []), [product.authors]);

    const authorNames = authors.map(a => MetadataUtils.getPerformerFullname(a)).join(', ');
    const authorIds = useMemo(() => authors.map(a => a.id!), [authors]);

    const productSpec = useMemo<ProductSpec>(
        () => ({
            loanFormat,
            filters: {
                author: { include: authorIds },
                productId: { exclude: [product.id] },
                ...languageFilter
            }
        }),
        [product, authorIds, languageFilter, loanFormat]
    );

    const seeAllUrl = buildSeeAllUrl({
        product,
        loanFormat,
        filter: productSpec.filters!,
        isMultiLanguageShopEnabled,
        shopLanguage
    });

    const isForDifferentLanguage = !!language && language !== product.language;

    const titlePrefix =
        loanFormat === productLoanFormat && !isForDifferentLanguage ? t('components.productDetails.more') : '';
    const moreFromContext = isForDifferentLanguage ? 'language' : 'product';

    const title = t(`components.productDetails.moreFrom_${moreFromContext}`, {
        titlePrefix,
        loanFormat: t(`components.productDetails.${loanFormat}`),
        language: t(`filters:language.${language}`, {
            defaultValue:
                isForDifferentLanguage && showLote ? t('menu.shop.shopLanguages.lote_dropdown') : t('otherLanguages')
        }),
        type: authorNames
    });

    if (!product.authors || product.authors.length === 0) {
        return null;
    }

    switch (dataSource) {
        case 'lote':
            return <LoteProductGroup productSpec={productSpec} seeAllUrl={seeAllUrl} title={title.trim()} />;
        case 'languagelearning':
            return (
                <LanguageLearningProductGroup
                    productSpec={productSpec}
                    seeAllUrl={seeAllUrl}
                    title={title.trim()}
                    shopLanguage={undefined}
                />
            );
        default:
            return (
                <CatalogueProductGroup
                    productSpec={productSpec}
                    seeAllUrl={seeAllUrl}
                    title={title.trim()}
                    shopLanguage={shopLanguage}
                />
            );
    }
};

type GenreProductGroupProps = {
    readonly loanFormat: LoanFormat;
    readonly product: ProductMetaData;
    readonly language?: string;
    readonly dataSource: 'catalogue' | 'lote' | 'languagelearning';
    shopLanguages?: string[];
    languageId?: string;
    showLote?: boolean;
};
const GenreProductGroup: FC<GenreProductGroupProps> = ({
    loanFormat,
    product,
    language,
    dataSource,
    shopLanguages,
    languageId,
    showLote
}) => {
    const languageFilter = useMemo(
        () => buildLanguageFilter(product, language, shopLanguages),
        [product, language, shopLanguages]
    );
    const { t } = useTranslation();
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);
    const shopLanguage = language === OTHER_LANGUAGES ? 'other' : languageId;

    const genreIds = useMemo(
        () =>
            product.genres
                ? // TODO [MultiLanguageShop] Filter "Language" related genres based on the selected language, when we receive genres translated from backend.
                  product.genres.filter(g => g.name !== 'Language').map(g => (g.subGenres ? g.subGenres[0].id : g.id))
                : [],
        [product.genres]
    );
    const localisedGenreIds = useMemo(
        () =>
            product.genres
                ? // TODO [MultiLanguageShop] Filter "Language" related genres based on the selected language, when we receive genres translated from backend.
                  product.genres
                      .filter(g => g.name !== 'Language')
                      .map(g => (g.subGenres && g.subGenres.length > 0 ? g.subGenres[0].name : g.name))
                      .join(', ')
                : [],
        [product.genres]
    );

    const productSpec: ProductSpec = useMemo(
        () => ({
            loanFormat,
            filters: {
                genre: { include: genreIds },
                productId: { exclude: [product.id] },
                ageCategory: product.ageCategory ? { include: [product.ageCategory] } : {},
                ...languageFilter
            },
            ageCategory: product.ageCategory
        }),
        [loanFormat, genreIds, product, languageFilter]
    );

    const seeAllUrl = buildSeeAllUrl({
        product,
        loanFormat,
        filter: productSpec.filters!,
        isMultiLanguageShopEnabled,
        shopLanguage
    });

    const isForDifferentLanguage = !!language && language !== product.language;
    const titlePrefix =
        loanFormat === LoanFormat.ePress && !isForDifferentLanguage ? t('components.productDetails.more') : '';
    const moreFromContext = isForDifferentLanguage ? 'language' : 'product';

    const title = t(`components.productDetails.moreFrom_${moreFromContext}`, {
        titlePrefix,
        loanFormat: t(`components.productDetails.${loanFormat}`),
        language: t(`filters:language.${language}`, {
            defaultValue:
                isForDifferentLanguage && showLote ? t('menu.shop.shopLanguages.lote_dropdown') : t('otherLanguages')
        }),
        type: localisedGenreIds
    });

    if (!product.genres || product.genres.length === 0) {
        return null;
    }

    switch (dataSource) {
        case 'lote':
            return <LoteProductGroup productSpec={productSpec} seeAllUrl={seeAllUrl} title={title.trim()} />;
        case 'languagelearning':
            return (
                <LanguageLearningProductGroup
                    productSpec={productSpec}
                    seeAllUrl={seeAllUrl}
                    title={title.trim()}
                    shopLanguage={undefined}
                />
            );
        default:
            return (
                <CatalogueProductGroup
                    productSpec={productSpec}
                    seeAllUrl={seeAllUrl}
                    title={title.trim()}
                    shopLanguage={shopLanguage}
                />
            );
    }
};

interface BuildSeeAllUrlProps {
    readonly product: ProductMetaData;
    readonly loanFormat: LoanFormat;
    readonly filter: { [key: string]: FilterSetting };
    isMultiLanguageShopEnabled: boolean;
    shopLanguage: string | undefined;
}

const buildSeeAllUrl = ({
    product,
    loanFormat,
    filter,
    isMultiLanguageShopEnabled,
    shopLanguage
}: BuildSeeAllUrlProps): To => {
    const mappedProductClass =
        isMultiLanguageShopEnabled && product.productClass === PRODUCT_CLASS_LOTE
            ? PRODUCT_CLASS_STANDARD
            : product.productClass;
    switch (mappedProductClass) {
        case PRODUCT_CLASS_STANDARD:
            return Utils.catalogueLocationDescriptorString({
                loanFormat,
                filter,
                shopLanguage,
                isMultiLanguageShopEnabled,
                ageCategory: undefined
            });
        case PRODUCT_CLASS_LOTE:
            return RoutingUtils.foreignLanguageDescriptorString({
                language: ALL_LANGUAGES,
                loanFormat,
                filter,
                ageCategory: undefined
            });
        case PRODUCT_CLASS_LANGUAGE_LEARNING:
            return RoutingUtils.languageLearningDescriptorString({
                language: ALL_LANGUAGES,
                loanFormat,
                filter,
                shopLanguage,
                isMultiLanguageShopEnabled,
                ageCategory: undefined
            });
        case PRODUCT_CLASS_CAMPAIGN:
            return Utils.catalogueLocationDescriptorString({
                loanFormat,
                filter,
                shopLanguage: undefined,
                isMultiLanguageShopEnabled,
                ageCategory: undefined
            });
        case PRODUCT_CLASS_LOCAL:
            return '';
    }
};

const buildLanguageFilter = (
    product: ProductMetaData,
    language?: string,
    shopLanguages?: string[]
): ProductFilter | undefined => {
    if (!language) return undefined;
    if (!product.language) return undefined;

    // language in this case is one of the main languages. We use path parameter to show the main language products, thus no need to use this language filter
    if (shopLanguages?.includes(language)) {
        return undefined;
    }

    if (product.language === language) {
        return { language: { include: [product.language] } };
    }

    return { language: { exclude: [product.language] } };
};
